import React, { useState } from 'react';
import api, { getCurrentUser } from '../services/api';

interface CustomerReplyProps {
  messageId: number;
  channel: 'SMS' | 'EMAIL';
  recipient: string;
  onClose: () => void;
  onSent?: (text: string) => void;
}

const CustomerReply: React.FC<CustomerReplyProps> = ({ messageId, channel, recipient, onClose, onSent }) => {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{type: 'success' | 'error', message: string} | null>(null);

  const handleSend = async () => {
    if (!replyText.trim()) return;
    
    setSending(true);
    setResult(null);
    try {
      const u = getCurrentUser();
      const response = await api.post(`/messages/${messageId}/reply`, {
        reply_text: replyText.trim(),
        userName: u.name || undefined,
        userEmail: u.email || undefined
      });
      
      if (response.data.success) {
        setResult({ type: 'success', message: `Reply sent via ${channel}` });
        if (onSent) onSent(replyText.trim());
        setReplyText('');
        setTimeout(() => onClose(), 2000);
      } else {
        setResult({ type: 'error', message: response.data.error || 'Send failed' });
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err.message || 'Failed to send reply';
      setResult({ type: 'error', message: errorMsg });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border-t border-gray-200 bg-blue-50 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-700">
          Reply via {channel} to {recipient}
        </h4>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <textarea
        value={replyText}
        onChange={(e) => setReplyText(e.target.value)}
        placeholder="Type your reply..."
        rows={3}
        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 mb-3"
      />
      
      {result && (
        <div className={`text-sm mb-3 ${result.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
          {result.message}
        </div>
      )}
      
      <div className="flex justify-end space-x-2">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSend}
          disabled={sending || !replyText.trim()}
          className="px-4 py-2 text-sm font-medium text-white bg-brand-700 rounded-lg hover:bg-brand-800 disabled:opacity-50"
        >
          {sending ? 'Sending...' : `Send ${channel === 'SMS' ? 'Text' : 'Email'}`}
        </button>
      </div>
    </div>
  );
};

export default CustomerReply;
